import EmailMovedBanner from "@/components/email/email-moved-banner";
// Campaign builder wizard — 3 steps:
//   1. Name + track
//   2. Brief (tone, voice, brand color, pacing, CTA philosophy, audience notes)
//   3. Generate (calls AI, navigates to detail page on success)

import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Sparkles, RefreshCw } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const GENERATION_STAGES = [
  "Loading codebase inventory (features, paths, screenshots)",
  "Sending brief + inventory to Rateek (the AI writer)",
  "Drafting Day 1 — welcome email",
  "Drafting Days 2–4 — core feature tour",
  "Drafting Days 5–7 — discovery & curated deals",
  "Drafting Days 8–10 — habits & checklist",
  "Validating CTA paths against real routes",
  "Saving 10 templates to the database",
];

const SEND_STAGES = [
  "Validating recipients",
  "De-duping against existing enrollments on this campaign",
  "Saving any per-day HTML overrides",
  "Creating subscription rows",
  "Rendering Day 1 HTML for each recipient",
  "Calling Resend to dispatch Day 1",
  "Recording send logs",
];

export { GENERATION_STAGES, SEND_STAGES };

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

const DEFAULT_BRIEF = {
  tone: "Direct, blunt, calm authority. Short sentences, plain English, James Clear style. No hype, no exclamation marks.",
  voice: "Rateek, the in-house onboarding coach for Favcy Select.",
  brandColor: "#FF5C00",
  pacingDaysCsv: "1,2,3,5,7,9,11,13,15,17",
  ctaPhilosophy: "One CTA per email pointing to a real page in the user's portal.",
  audienceNotes: "",
};

export default function AdminCampaignNew() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [track, setTrack] = useState<"investor" | "banker">("investor");
  const [brief, setBrief] = useState({ ...DEFAULT_BRIEF });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Auto-scroll the page to the talking loader when generation starts so the
  // admin can't miss it on tall viewports — this was the "no loader came"
  // complaint after generate clicks landed below the fold.
  const loaderRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (busy === "create" || busy === "generate") {
      loaderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [busy]);

  const canStep2 = name.trim().length > 0;
  const canStep3 = canStep2;

  // Two-stage flow that survives the platform's ~30s upstream proxy timeout:
  //   1. POST /campaigns — fast (DB insert, <1s)
  //   2. POST /campaigns/:id/generate — kicks off background work, returns 202 immediately
  //   3. Poll GET /campaigns/:id every 2s until generationStatus is 'complete' or 'failed'
  // This is the architecture fix for the "upstream request timeout" error
  // — generation routinely takes 60-90s for 10 emails and the proxy was
  // killing the connection well before that.
  const handleCreateAndGenerate = async () => {
    setBusy("create"); setError(null); setInfo(null);
    let id: number | null = null;
    try {
      const created = await api<{ campaign: { id: number } }>("/campaigns", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), track, brief }),
      });
      id = created.campaign.id;
      setInfo(`Campaign created (id ${id}). Kicking off generation...`);
      setBusy("generate");
      await api(`/campaigns/${id}/generate`, { method: "POST" });
    } catch (err: any) {
      setError(err?.message || "Create / kick-off failed");
      setBusy(null);
      return;
    }

    // Poll. Generation usually finishes in 30-90s; we cap at 5 minutes so a
    // hung OpenAI call doesn't keep the spinner forever.
    const startedAt = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    while (Date.now() - startedAt < TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const polled = await api<{ campaign: { generationStatus: string; generationError: string | null } }>(`/campaigns/${id}`);
        const s = polled.campaign.generationStatus;
        if (s === "complete") {
          setInfo("Generated. Opening review...");
          setLocation(`/admin/campaigns/${id}`);
          return;
        }
        if (s === "failed") {
          setError(polled.campaign.generationError || "Favcy Navigator is not responding right now. This is usually a transient issue — please click Retry after 10 mins.");
          setBusy(null);
          // Still navigate so user sees the failed campaign and can retry from there.
          setTimeout(() => setLocation(`/admin/campaigns/${id}`), 1500);
          return;
        }
        // else still 'generating' — loop
      } catch {
        // Transient poll error — keep trying. Don't surface to user unless we time out.
      }
    }
    setError("Generation is taking longer than expected. Open the campaign — it may still finish in the background.");
    setBusy(null);
    setTimeout(() => setLocation(`/admin/campaigns/${id}`), 1500);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <EmailMovedBanner />
      <div>
        <h1 className="text-2xl font-serif tracking-tight">Build a campaign</h1>
        <p className="text-sm text-muted-foreground mt-1">3 steps: name your campaign, tell Rateek how it should feel, then generate 10 emails grounded in the codebase inventory.</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-xs">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`flex items-center gap-2 ${step === n ? "text-[#FF5C00] font-semibold" : step > n ? "text-emerald-700" : "text-muted-foreground"}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono border ${
              step === n ? "border-[#FF5C00] bg-[#FF5C00]/10" :
              step > n ? "border-emerald-600 bg-emerald-500/10" : "border-border"
            }`}>{n}</span>
            <span>{n === 1 ? "Name" : n === 2 ? "Brief" : "Generate"}</span>
            {n < 3 && <ChevronRight className="w-3 h-3" />}
          </div>
        ))}
      </div>

      {error && <div className="p-3 rounded-md text-sm border bg-red-500/10 border-red-500/30 text-red-700">{error}</div>}
      {info && !busy && <div className="p-3 rounded-md text-sm border bg-emerald-500/10 border-emerald-500/30 text-emerald-700">{info}</div>}

      <div ref={loaderRef}>
        <ProgressLoader
          active={busy === "generate"}
          title="Rateek is drafting your 10 emails"
          stages={GENERATION_STAGES}
          stageIntervalMs={4500}
          note="This takes 20-60 seconds. Don't refresh — the page will switch to the review screen automatically."
        />
        <ProgressLoader
          active={busy === "create"}
          title="Saving the campaign"
          stages={["Validating brief", "Inserting campaign row"]}
          stageIntervalMs={1200}
        />
      </div>


      <div className="rounded-lg border border-border p-5 space-y-4">
        {step === 1 && (
          <>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Campaign name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Opening 10"
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background" autoFocus />
              <p className="text-xs text-muted-foreground mt-1">Internal name only — not shown to recipients.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Audience track</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {(["investor", "banker"] as const).map((t) => (
                  <button key={t} onClick={() => setTrack(t)}
                    className={`px-3 py-2 text-sm rounded-md border ${track === t ? "border-[#FF5C00] bg-[#FF5C00]/10 text-[#FF5C00]" : "border-border hover:bg-muted"}`}>
                    {t === "investor" ? "Investors" : "Advisors (Bankers)"}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <Field label="Tone" value={brief.tone} onChange={(v) => setBrief({ ...brief, tone: v })} multiline
              hint="How the email should feel. Voice & style guidance for Rateek." />
            <Field label="Voice (the persona writing it)" value={brief.voice} onChange={(v) => setBrief({ ...brief, voice: v })}
              hint='e.g. "Rateek, the in-house onboarding coach for Favcy Select."' />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand color" value={brief.brandColor} onChange={(v) => setBrief({ ...brief, brandColor: v })}
                hint="Hex code used for buttons & accents" />
              <Field label="Pacing (days, CSV)" value={brief.pacingDaysCsv} onChange={(v) => setBrief({ ...brief, pacingDaysCsv: v })}
                hint="When each email lands relative to D0" />
            </div>
            <Field label="CTA philosophy" value={brief.ctaPhilosophy} onChange={(v) => setBrief({ ...brief, ctaPhilosophy: v })}
              hint="What every CTA should do" />
            <Field label="Audience notes (optional)" value={brief.audienceNotes} onChange={(v) => setBrief({ ...brief, audienceNotes: v })} multiline
              hint="Anything specific about who's receiving this — context helps Rateek." />
          </>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold">Review brief, then generate</h2>
            <ReviewRow label="Name" value={name} />
            <ReviewRow label="Track" value={track === "investor" ? "Investors" : "Advisors (Bankers)"} />
            <ReviewRow label="Tone" value={brief.tone} />
            <ReviewRow label="Voice" value={brief.voice} />
            <ReviewRow label="Brand color" value={brief.brandColor} />
            <ReviewRow label="Pacing" value={brief.pacingDaysCsv} />
            <ReviewRow label="CTA philosophy" value={brief.ctaPhilosophy} />
            {brief.audienceNotes && <ReviewRow label="Audience notes" value={brief.audienceNotes} />}
            <div className="pt-3 border-t border-border">
              <button
                onClick={handleCreateAndGenerate}
                disabled={!!busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50"
              >
                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy === "create" ? "Creating..." : busy === "generate" ? "Generating — see progress above" : "Create & generate 10 emails"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep((s) => (s > 1 ? ((s - 1) as any) : s))}
          disabled={step === 1 || !!busy}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 && (
          <button
            onClick={() => setStep((s) => ((s + 1) as any))}
            disabled={(step === 1 && !canStep2) || (step === 2 && !canStep3)}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-foreground text-background hover:opacity-90 disabled:opacity-30"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, hint }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean; hint?: string }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background" />
      )}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-foreground whitespace-pre-wrap">{value}</span>
    </div>
  );
}
